import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Globe,
  MoreHorizontal,
  Trash2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Activity,
  Eye,
  ArrowLeft,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface ScanSecurityHeaders {
  xFrameOptions: boolean
  xContentTypeOptions: boolean
  strictTransportSecurity: boolean
  contentSecurityPolicy: boolean
  xXssProtection: boolean
  referrerPolicy: boolean
}

interface ScanResult {
  scannedAt: string
  wpVersionDetected: string | null
  restApiAccessible: boolean
  loginPageFound: boolean
  adminPageFound: boolean
  readmeFound: boolean
  generatorTag: string | null
  sslValid: boolean
  sslExpiry: string | null
  sslIssuer: string | null
  securityHeaders: ScanSecurityHeaders
  httpStatusCode: number
  responseTimeMs: number
  detectedPlugins: Array<{
    name: string
    version: string | null
    slug: string | null
    latestVersion: string | null
    isOutdated: boolean
  }>
  vulnerabilities: Array<{
    id: string
    title: string
    severity: 'low' | 'medium' | 'high' | 'critical'
    affectedComponent: string
    affectedVersions: string
    fixedIn: string | null
    references: string[]
  }>
}

interface WordPressSite {
  id: string
  tenantId: string
  clientId: string | null
  url: string
  name: string
  wpVersion: string | null
  phpVersion: string | null
  theme: string | null
  plugins: unknown[] | null
  vulnerabilities: unknown[] | null
  sslStatus: 'valid' | 'expiring' | 'expired' | 'none' | null
  uptime: boolean
  lastScanAt: string | null
  lastScanResult: ScanResult | null
  alertsEnabled: boolean
  createdAt: string
  updatedAt: string
}

interface WordPressStats {
  totalSites: number
  sitesUp: number
  sitesDown: number
  sslValid: number
  sslExpiring: number
  sslExpired: number
  sslNone: number
  vulnerableSites: number
  outdatedSites: number
  neverScanned: number
}

// ============================================
// SSL Status Helpers
// ============================================

const sslConfig: Record<string, { label: string; icon: typeof ShieldCheck; className: string }> = {
  valid: { label: 'Valid', icon: ShieldCheck, className: 'text-green-500' },
  expiring: { label: 'Expiring', icon: ShieldAlert, className: 'text-yellow-500' },
  expired: { label: 'Expired', icon: ShieldX, className: 'text-red-500' },
  none: { label: 'None', icon: Shield, className: 'text-muted-foreground' },
}

function SslBadge({ status }: { status: string | null }) {
  const config = sslConfig[status || 'none'] || sslConfig['none']
  const Icon = config.icon
  return (
    <Badge variant="outline" className={`gap-1 ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  )
}

function HealthIndicator({ uptime }: { uptime: boolean }) {
  return uptime ? (
    <div className="flex items-center gap-1 text-green-500">
      <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
      <span className="text-xs">Online</span>
    </div>
  ) : (
    <div className="flex items-center gap-1 text-red-500">
      <div className="h-2 w-2 rounded-full bg-red-500" />
      <span className="text-xs">Down</span>
    </div>
  )
}

// ============================================
// Add Site Dialog
// ============================================

function AddSiteDialog() {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [name, setName] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/wordpress/sites', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wordpress-sites'] })
      queryClient.invalidateQueries({ queryKey: ['wordpress-stats'] })
      toast({ title: 'WordPress site added' })
      setOpen(false)
      setUrl('')
      setName('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to add site', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Add Site</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add WordPress Site</DialogTitle>
          <DialogDescription>Add a WordPress site to monitor for version updates, vulnerabilities, and health.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Site Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Client's Blog" />
          </div>
          <div className="space-y-2">
            <Label>URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({ url, name })}
            disabled={!url || !name || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding...' : 'Add Site'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Site Detail View
// ============================================

function SiteDetailView({ site, onBack }: { site: WordPressSite; onBack: () => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const scanMutation = useMutation({
    mutationFn: () => apiClient.post(`/wordpress/sites/${site.id}/scan`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wordpress-sites'] })
      queryClient.invalidateQueries({ queryKey: ['wordpress-site', site.id] })
      queryClient.invalidateQueries({ queryKey: ['wordpress-stats'] })
      toast({ title: 'Scan completed' })
    },
    onError: () => toast({ title: 'Error', description: 'Scan failed', variant: 'destructive' }),
  })

  const { data: siteData } = useQuery({
    queryKey: ['wordpress-site', site.id],
    queryFn: () => apiClient.get<WordPressSite>(`/wordpress/sites/${site.id}`),
    initialData: { success: true, data: site } as any,
  })

  const currentSite = siteData?.data ?? site
  const scanResult = currentSite.lastScanResult as ScanResult | null

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold">{currentSite.name}</h2>
          <a href={currentSite.url} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1">
            {currentSite.url} <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="flex items-center gap-2">
          <HealthIndicator uptime={currentSite.uptime} />
          <SslBadge status={currentSite.sslStatus} />
          <Button onClick={() => scanMutation.mutate()} disabled={scanMutation.isPending}>
            <RefreshCw className={`mr-2 h-4 w-4 ${scanMutation.isPending ? 'animate-spin' : ''}`} />
            {scanMutation.isPending ? 'Scanning...' : 'Scan Now'}
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">WordPress Version</div>
            <div className="text-xl font-bold mt-1">{currentSite.wpVersion || 'Unknown'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">PHP Version</div>
            <div className="text-xl font-bold mt-1">{currentSite.phpVersion || 'Unknown'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Theme</div>
            <div className="text-xl font-bold mt-1">{currentSite.theme || 'Unknown'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Last Scanned</div>
            <div className="text-xl font-bold mt-1">
              {currentSite.lastScanAt ? new Date(currentSite.lastScanAt).toLocaleString() : 'Never'}
            </div>
          </CardContent>
        </Card>
      </div>

      {scanResult ? (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Security Headers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4" /> Security Headers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(scanResult.securityHeaders).map(([header, present]) => (
                  <div key={header} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {header.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim()}
                    </span>
                    {present ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Scan Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" /> Scan Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">HTTP Status</span>
                  <Badge variant={scanResult.httpStatusCode === 200 ? 'outline' : 'destructive'}>
                    {scanResult.httpStatusCode}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Response Time</span>
                  <span>{scanResult.responseTimeMs}ms</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">REST API</span>
                  {scanResult.restApiAccessible ? (
                    <Badge variant="outline" className="text-green-500">Accessible</Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">Not Found</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Login Page</span>
                  {scanResult.loginPageFound ? (
                    <Badge variant="outline" className="text-yellow-500">Exposed</Badge>
                  ) : (
                    <Badge variant="outline" className="text-green-500">Hidden</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Admin Page</span>
                  {scanResult.adminPageFound ? (
                    <Badge variant="outline" className="text-yellow-500">Exposed</Badge>
                  ) : (
                    <Badge variant="outline" className="text-green-500">Hidden</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Readme.html</span>
                  {scanResult.readmeFound ? (
                    <Badge variant="outline" className="text-yellow-500">Exposed</Badge>
                  ) : (
                    <Badge variant="outline" className="text-green-500">Removed</Badge>
                  )}
                </div>
                {scanResult.generatorTag && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Generator Tag</span>
                    <span className="text-xs font-mono">{scanResult.generatorTag}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Detected Plugins */}
          {scanResult.detectedPlugins.length > 0 && (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Detected Plugins ({scanResult.detectedPlugins.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 md:grid-cols-2">
                  {scanResult.detectedPlugins.map((plugin, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-md bg-muted">
                      <div>
                        <div className="font-medium text-sm">{plugin.name}</div>
                        <div className="text-xs text-muted-foreground">{plugin.version || 'Unknown version'}</div>
                      </div>
                      {plugin.isOutdated ? (
                        <Badge variant="destructive" className="text-xs">Outdated</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-green-500">Current</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vulnerabilities */}
          {scanResult.vulnerabilities.length > 0 && (
            <Card className="md:col-span-2 border-destructive">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Vulnerabilities ({scanResult.vulnerabilities.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {scanResult.vulnerabilities.map((vuln, i) => (
                    <div key={i} className="p-3 rounded-md bg-destructive/5 border border-destructive/20">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{vuln.title}</span>
                        <Badge variant={vuln.severity === 'critical' ? 'destructive' : vuln.severity === 'high' ? 'destructive' : 'secondary'}>
                          {vuln.severity}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {vuln.affectedComponent} ({vuln.affectedVersions})
                        {vuln.fixedIn && <> &mdash; Fixed in {vuln.fixedIn}</>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center py-12">
            <RefreshCw className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No scan results yet</p>
            <p className="text-sm text-muted-foreground mt-1">Click "Scan Now" to perform the first scan</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ============================================
// Main Page
// ============================================

export function WordPressPage() {
  const [search, setSearch] = useState('')
  const [sslFilter, setSslFilter] = useState<string>('all')
  const [selectedSite, setSelectedSite] = useState<WordPressSite | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: sitesData, isLoading } = useQuery({
    queryKey: ['wordpress-sites', sslFilter],
    queryFn: () => apiClient.get<{ data: WordPressSite[]; meta: { pagination: unknown } }>(
      '/wordpress/sites',
      {
        limit: 100,
        ...(sslFilter !== 'all' ? { sslStatus: sslFilter } : {}),
      }
    ),
  })

  const { data: statsData } = useQuery({
    queryKey: ['wordpress-stats'],
    queryFn: () => apiClient.get<WordPressStats>('/wordpress/stats'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/wordpress/sites/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wordpress-sites'] })
      queryClient.invalidateQueries({ queryKey: ['wordpress-stats'] })
      toast({ title: 'Site removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to remove site', variant: 'destructive' }),
  })

  const scanMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/wordpress/sites/${id}/scan`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wordpress-sites'] })
      queryClient.invalidateQueries({ queryKey: ['wordpress-stats'] })
      toast({ title: 'Scan completed' })
    },
    onError: () => toast({ title: 'Error', description: 'Scan failed', variant: 'destructive' }),
  })

  const sites: WordPressSite[] = sitesData?.data ?? (sitesData as any)?.data?.data ?? []
  const stats = statsData?.data ?? null

  const filtered = useMemo(() => {
    if (!search) return sites
    const lower = search.toLowerCase()
    return sites.filter(
      (s) =>
        s.name.toLowerCase().includes(lower) ||
        s.url.toLowerCase().includes(lower)
    )
  }, [sites, search])

  // Show detail view if a site is selected
  if (selectedSite) {
    return <SiteDetailView site={selectedSite} onBack={() => setSelectedSite(null)} />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">WordPress Monitoring</h1>
          <p className="text-muted-foreground">Monitor WordPress sites for versions, vulnerabilities, and health</p>
        </div>
        <AddSiteDialog />
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{stats?.totalSites ?? 0}</div>
                <p className="text-xs text-muted-foreground">Total Sites</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.sitesUp ?? 0}</div>
                <p className="text-xs text-muted-foreground">Online</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <div>
                <div className="text-2xl font-bold text-red-500">{stats?.sitesDown ?? 0}</div>
                <p className="text-xs text-muted-foreground">Down</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-yellow-500" />
              <div>
                <div className="text-2xl font-bold text-yellow-500">{stats?.vulnerableSites ?? 0}</div>
                <p className="text-xs text-muted-foreground">Vulnerable</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.sslValid ?? 0}</div>
                <p className="text-xs text-muted-foreground">SSL Valid</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or URL..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={sslFilter} onValueChange={setSslFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All SSL" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All SSL Status</SelectItem>
            <SelectItem value="valid">SSL Valid</SelectItem>
            <SelectItem value="expiring">SSL Expiring</SelectItem>
            <SelectItem value="expired">SSL Expired</SelectItem>
            <SelectItem value="none">No SSL</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Site Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
              <CardContent><Skeleton className="h-20 w-full" /></CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12">
            <Globe className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No WordPress sites found</p>
            <p className="text-sm text-muted-foreground mt-1">Add a site to start monitoring</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((site) => (
            <Card key={site.id} className={`transition-all hover:shadow-md ${!site.uptime ? 'border-red-200 dark:border-red-900' : ''}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                  <CardTitle className="text-base truncate">{site.name}</CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  <HealthIndicator uptime={site.uptime} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setSelectedSite(site)}>
                        <Eye className="mr-2 h-4 w-4" /> View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => scanMutation.mutate(site.id)}>
                        <RefreshCw className="mr-2 h-4 w-4" /> Scan Now
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(site.id)}>
                        <Trash2 className="mr-2 h-4 w-4" /> Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <a href={site.url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mb-3 truncate">
                  {site.url} <ExternalLink className="h-3 w-3 shrink-0" />
                </a>

                <div className="flex flex-wrap gap-2 mb-3">
                  <SslBadge status={site.sslStatus} />
                  {site.wpVersion && (
                    <Badge variant="outline" className="text-xs">WP {site.wpVersion}</Badge>
                  )}
                  {!site.wpVersion && (
                    <Badge variant="secondary" className="text-xs">Version Unknown</Badge>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {site.lastScanAt ? (
                      <span>Scanned {new Date(site.lastScanAt).toLocaleDateString()}</span>
                    ) : (
                      <span>Never scanned</span>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setSelectedSite(site)}>
                    Details <ArrowLeft className="h-3 w-3 rotate-180" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
